var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DropOffMode } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { ShowModelIndicatorMessage } from "@messages/indicator";
import { DropOffPreviewManager } from "./DropOffPreviewManager";
import { DropOffAreaManager } from "./DropOffAreaManager";
import { DropOffTargetManager } from "./DropOffTargetManager";
/**
 * This service is responsible for checking if an attachment can be dropped. There are three possible constraints for
 * dropping an attachment, this constraint is specified in the `PickupFacet`. It can either be reset to its original
 * position, inside a drop-off area or be above of a drop-off surface while being inside a drop-off area.
 *
 * In case the constraint is set to drop-off area, then the service is also responsible for displaying a preview model
 * of the held model at the projected drop-off position.
 */
export class DropOffService {
    constructor(connection) {
        this.connection = connection;
        this.controllerDropOffTargetInfos = new Map();
        this.modelPickupInformation = new Map();
        /// Handler methods ///
        this.onEnterDropOffArea = (collision) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const modelUID = collision.modelUID;
            const pickupInformation = this.modelPickupInformation.get(modelUID);
            if (pickupInformation === undefined)
                return;
            const dropOffTarget = this.findDropOffTarget(pickupInformation.controllerUID, collision.dropOffAreaUID, true);
            if (dropOffTarget === undefined)
                return;
            dropOffTarget.dropOffAreaUID = collision.dropOffAreaUID;
            if (pickupInformation.previewID !== undefined)
                dropOffTarget.previewID = pickupInformation.previewID;
            // No dedicated target: raycast the drop-off area itself. Preview placement is resolved after the hit.
            dropOffTarget.targetUID = (_a = this.dropOffAreaManager.getDropOffAreaTargetUID(collision.dropOffAreaUID)) !== null && _a !== void 0 ? _a : collision.dropOffAreaUID;
            dropOffTarget.canBeDropped = false;
            void this.syncDropOffTargetPreview(modelUID, dropOffTarget);
        });
        this.onLeaveDropOffArea = (collision) => __awaiter(this, void 0, void 0, function* () {
            const pickupInformation = this.modelPickupInformation.get(collision.modelUID);
            if (pickupInformation === undefined)
                return;
            const dropOffTarget = this.findDropOffTarget(pickupInformation.controllerUID, collision.dropOffAreaUID);
            if (dropOffTarget === undefined)
                return;
            dropOffTarget.canBeDropped = false;
            if (pickupInformation.previewID !== undefined)
                yield this.dropOffPreviewManager.hidePreview(pickupInformation.previewID);
            this.onChangeDropState(pickupInformation.modelUID);
        });
        this.onMoveInsideDropOffArea = (collision) => __awaiter(this, void 0, void 0, function* () {
            const modelUID = collision.modelUID;
            const pickupInformation = this.modelPickupInformation.get(modelUID);
            if (pickupInformation === undefined)
                return;
            // We need a drop-off target info object in order to update its information
            const dropOffTarget = this.findDropOffTarget(pickupInformation.controllerUID, collision.dropOffAreaUID);
            if (dropOffTarget === undefined)
                return;
            // This should not happen, by here we should have a preview
            const previewID = dropOffTarget.previewID;
            if (previewID === undefined)
                return;
            // Keep move events lightweight; central sync method handles show/hide/update + state transitions.
            void this.syncDropOffTargetPreview(modelUID, dropOffTarget);
        });
        this.onChangeDropState = (modelUID) => {
            const modelPickupInformation = this.modelPickupInformation.get(modelUID);
            if (modelPickupInformation === undefined)
                return;
            // If we have a valid drop-off target, then we can drop this model
            const validDropOffTarget = this.getValidDropOffTarget(modelPickupInformation);
            const canDropOff = validDropOffTarget !== undefined;
            this.connection.send(new ShowModelIndicatorMessage(modelPickupInformation.controllerUID, "img/drop.png", modelPickupInformation.showDropOffIcon === true && canDropOff, modelPickupInformation.dropOffButton));
        };
        this.dropOffPreviewManager = new DropOffPreviewManager();
        this.dropOffAreaManager = new DropOffAreaManager(this.connection);
        this.dropOffTargetManager = new DropOffTargetManager(this.connection);
        this.dropOffAreaManager.onEnterDropOffArea.add(this.onEnterDropOffArea);
        this.dropOffAreaManager.onLeaveDropOffArea.add(this.onLeaveDropOffArea);
        this.dropOffAreaManager.onMoveInsideDropOffArea.add(this.onMoveInsideDropOffArea);
    }
    /// Getter and setter ///
    addPickup(pickup) {
        var _a;
        // We only add this target if we can drop the model from the beginning. This is the case when the model can be
        // dropped anytime
        const dropOffTargets = new Set();
        const dropToPrevPosition = pickup.dropOffMode === DropOffMode.PREVIOUS_POSITION;
        const dropInPlace = pickup.dropOffMode === DropOffMode.ALWAYS;
        const canBeDropped = dropToPrevPosition || dropInPlace;
        if (canBeDropped) {
            const dropOffTarget = this.createInitialDropOffTarget();
            dropOffTarget.canBeDropped = true;
            dropOffTargets.add(dropOffTarget);
        }
        this.controllerDropOffTargetInfos.set(pickup.controllerUID, dropOffTargets);
        this.modelPickupInformation.set(pickup.modelUID, pickup);
        if (pickup.dropOffMode === DropOffMode.DROP_OFF_AREA) {
            this.dropOffAreaManager.addModel(pickup.modelUID, (_a = pickup.dropOffLayers) !== null && _a !== void 0 ? _a : []);
            if (pickup.previewID === undefined) {
                // Create one shared preview per pickup; all active drop-off targets reference this same preview.
                pickup.previewID = this.createPickupPreviewID(pickup.modelUID, pickup.controllerUID);
                void this.dropOffPreviewManager.createPreview(pickup.previewID, pickup.modelUID, pickup.controllerUID)
                    .then(() => this.syncPickupPreview(pickup.modelUID));
            }
        }
        else
            this.onChangeDropState(pickup.modelUID);
    }
    removePickup(pickup) {
        this.removeAllDropOffTargetsForController(pickup.controllerUID);
        this.onChangeDropState(pickup.modelUID);
        if (pickup.previewID !== undefined) {
            this.dropOffPreviewManager.removePreview(pickup.previewID);
            pickup.previewID = undefined;
        }
        this.controllerDropOffTargetInfos.delete(pickup.controllerUID);
        this.modelPickupInformation.delete(pickup.modelUID);
        this.dropOffAreaManager.removeModel(pickup.modelUID);
    }
    refreshPickup(pickup) {
        // Re-evaluate existing contacts so entering/picking up while already inside an area updates immediately.
        this.dropOffAreaManager.refreshContacts();
        void this.syncPickupPreview(pickup.modelUID);
    }
    /**
     * Returns a valid drop-off target that can be dropped onto at this moment. In case multiple targets are available,
     * then we choose the last one in the ordered set. The idea here is that the most recent drop-off target must be last
     */
    getValidDropOffTarget(pickup) {
        const dropOffTargets = this.controllerDropOffTargetInfos.get(pickup.controllerUID);
        if (dropOffTargets === undefined)
            return undefined;
        let lastValidTarget;
        for (const dropOffTarget of dropOffTargets.values()) {
            if (dropOffTarget.canBeDropped)
                lastValidTarget = dropOffTarget;
        }
        return lastValidTarget;
    }
    /// Private drop-off targets methods ///
    createInitialDropOffTarget() {
        return {
            active: false,
            canBeDropped: false,
            targetUID: undefined,
            worldTransform: new Matrix4(),
            previewID: undefined
        };
    }
    /**
     * Remove all drop-off targets from controller. The shared pickup preview is removed separately.
     */
    removeAllDropOffTargetsForController(controllerUID) {
        const dropOffTargets = this.controllerDropOffTargetInfos.get(controllerUID);
        dropOffTargets === null || dropOffTargets === void 0 ? void 0 : dropOffTargets.clear();
    }
    findDropOffTarget(controllerUID, dropOffAreaUID, createIfUnavailable = false) {
        const dropOffTargets = this.controllerDropOffTargetInfos.get(controllerUID);
        if (dropOffTargets === undefined)
            return undefined;
        for (const dropOffTarget of dropOffTargets.values()) {
            if (dropOffTarget.dropOffAreaUID === dropOffAreaUID)
                return dropOffTarget;
        }
        if (createIfUnavailable) {
            const dropOffTarget = this.createInitialDropOffTarget();
            dropOffTargets.add(dropOffTarget);
            return dropOffTarget;
        }
        return undefined;
    }
    createPickupPreviewID(modelUID, controllerUID) {
        // UID-scoped so two held models never share a preview, even when names match.
        return `${modelUID}-${controllerUID}`;
    }
    syncPickupPreview(modelUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const pickupInformation = this.modelPickupInformation.get(modelUID);
            if (pickupInformation === undefined || pickupInformation.previewID === undefined)
                return;
            const dropOffTargets = this.controllerDropOffTargetInfos.get(pickupInformation.controllerUID);
            if (dropOffTargets === undefined)
                return;
            // Fan out a single pickup update to all active drop-off targets owned by this controller.
            for (const dropOffTarget of dropOffTargets.values()) {
                if (dropOffTarget.dropOffAreaUID === undefined)
                    continue;
                if (dropOffTarget.previewID === undefined)
                    dropOffTarget.previewID = pickupInformation.previewID;
                if (dropOffTarget.previewID !== pickupInformation.previewID)
                    continue;
                if (dropOffTarget.targetUID !== undefined)
                    yield this.syncDropOffTargetPreview(modelUID, dropOffTarget);
            }
        });
    }
    syncDropOffTargetPreview(modelUID, dropOffTarget) {
        return __awaiter(this, void 0, void 0, function* () {
            const pickupInformation = this.modelPickupInformation.get(modelUID);
            if (pickupInformation === undefined)
                return;
            const previewID = pickupInformation.previewID;
            if (previewID === undefined)
                return;
            // Guard against async races: preview can disappear while target computations are still in flight.
            const previewModel = this.dropOffPreviewManager.getPreviewModel(previewID);
            if (previewModel === undefined)
                return;
            if (dropOffTarget.targetUID === undefined)
                return;
            const transform = yield this.computeDropOffTransformForDropOffTarget(modelUID, dropOffTarget.targetUID);
            if (this.modelPickupInformation.get(modelUID) !== pickupInformation || pickupInformation.previewID !== previewID)
                return;
            if (this.dropOffPreviewManager.getPreviewModel(previewID) !== previewModel)
                return;
            // The area may have been left while the raycast was in flight; the leave handler already hid the preview.
            if (!this.isStillInDropOffArea(modelUID, dropOffTarget))
                return;
            if (transform !== undefined) {
                // Snapshot before any further await. Concurrent hand updates must not reuse this matrix.
                const raycastWorld = transform.world.clone();
                if (!dropOffTarget.canBeDropped) {
                    yield this.dropOffPreviewManager.showPreview(previewID);
                    if (this.dropOffPreviewManager.getPreviewModel(previewID) !== previewModel || this.modelPickupInformation.get(modelUID) !== pickupInformation)
                        return;
                    if (!this.isStillInDropOffArea(modelUID, dropOffTarget)) {
                        yield this.dropOffPreviewManager.hidePreview(previewID);
                        return;
                    }
                    dropOffTarget.canBeDropped = true;
                    this.onChangeDropState(pickupInformation.modelUID);
                }
                const previewWorld = this.resolvePreviewWorldTransform(modelUID, dropOffTarget, raycastWorld);
                yield this.dropOffPreviewManager.updatePreview(previewID, previewWorld);
                if (this.dropOffPreviewManager.getPreviewModel(previewID) !== previewModel || this.modelPickupInformation.get(modelUID) !== pickupInformation)
                    return;
                if (!this.isStillInDropOffArea(modelUID, dropOffTarget))
                    return;
                // Use the preview's final transform (after movement constraints) for consistent drop result.
                const previewWorldTransform = this.dropOffPreviewManager.getPreviewWorldTransform(previewID);
                const previewLocalTransform = previewWorldTransform !== undefined
                    ? this.computeRelativeDropOffTransform(previewWorldTransform, dropOffTarget.targetUID)
                    : undefined;
                dropOffTarget.worldTransform.copy(previewLocalTransform !== null && previewLocalTransform !== void 0 ? previewLocalTransform : transform.local);
            }
            else if (dropOffTarget.canBeDropped) {
                yield this.dropOffPreviewManager.hidePreview(previewID);
                if (this.dropOffPreviewManager.getPreviewModel(previewID) !== previewModel || this.modelPickupInformation.get(modelUID) !== pickupInformation)
                    return;
                dropOffTarget.canBeDropped = false;
                this.onChangeDropState(pickupInformation.modelUID);
            }
        });
    }
    isStillInDropOffArea(modelUID, dropOffTarget) {
        return dropOffTarget.dropOffAreaUID !== undefined
            && this.dropOffAreaManager.isInsideDropOffArea(modelUID, dropOffTarget.dropOffAreaUID);
    }
    /**
     * Raycast hit is only the gate. A destination position wins. Without a dedicated drop-off target and without a
     * destination, the preview sits at the drop-off area's world pose.
     */
    resolvePreviewWorldTransform(modelUID, dropOffTarget, raycastWorld) {
        var _a, _b, _c;
        const dropOffAreaUID = dropOffTarget.dropOffAreaUID;
        if (dropOffAreaUID === undefined)
            return raycastWorld;
        const positionUID = this.dropOffAreaManager.getDestinationPositionUID(dropOffAreaUID, modelUID);
        if (positionUID !== undefined)
            return (_b = (_a = this.dropOffTargetManager.retrieveModelWorldTransform(positionUID)) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : raycastWorld;
        if (this.dropOffAreaManager.getDropOffAreaTargetUID(dropOffAreaUID) !== undefined)
            return raycastWorld;
        return (_c = this.computeAreaPreviewWorldTransform(modelUID, dropOffAreaUID)) !== null && _c !== void 0 ? _c : raycastWorld;
    }
    /** Area position and orientation, held-model scale, so the collider scale is not inherited. */
    computeAreaPreviewWorldTransform(modelUID, dropOffAreaUID) {
        var _a;
        const areaWorld = this.dropOffTargetManager.retrieveModelWorldTransform(dropOffAreaUID);
        if (areaWorld === undefined)
            return undefined;
        const position = new Vector3();
        const orientation = new Quaternion();
        areaWorld.decompose(position, orientation, new Vector3());
        const scale = new Vector3(1, 1, 1);
        (_a = this.dropOffTargetManager.retrieveModelWorldTransform(modelUID)) === null || _a === void 0 ? void 0 : _a.decompose(new Vector3(), new Quaternion(), scale);
        return new Matrix4().compose(position, orientation, scale);
    }
    /// Private transform helper methods ///
    computeDropOffTransformForDropOffTarget(modelUID, dropOffTargetUID) {
        return __awaiter(this, void 0, void 0, function* () {
            const pickupInformation = this.modelPickupInformation.get(modelUID);
            if (pickupInformation === undefined)
                return undefined;
            const world = yield this.dropOffTargetManager.computeDropOffWorldTransform(modelUID, dropOffTargetUID, pickupInformation.initialOrientation, pickupInformation.controllerUID);
            if (world === undefined)
                return undefined;
            const local = this.computeRelativeDropOffTransform(world, dropOffTargetUID);
            if (local === undefined)
                return undefined;
            return { world, local };
        });
    }
    computeRelativeDropOffTransform(childTransform, parentUid) {
        const childWorldMatrix = childTransform;
        const parentWorldMatrix = this.dropOffTargetManager.retrieveModelWorldTransform(parentUid);
        if (parentWorldMatrix === undefined || childWorldMatrix === undefined)
            return undefined;
        return parentWorldMatrix.clone().invert().multiply(childWorldMatrix);
    }
}
